import {
  initializeFirestore,
  getFirestore as _getFirestore,
  collection as _collection,
  doc as _doc,
  query as _query,
  where as _where,
  limit as _limit,
  getDocs as _getDocs,
  getDoc as _getDoc,
  addDoc as _addDoc,
  updateDoc as _updateDoc,
  deleteDoc as _deleteDoc,
  onSnapshot as _onSnapshot,
  serverTimestamp as _serverTimestamp,
  writeBatch as _writeBatch,
  setDoc as _setDoc,
  Timestamp,
  arrayUnion as _arrayUnion,
  arrayRemove as _arrayRemove,
  deleteField as _deleteField,
} from 'firebase/firestore';
import app from './firebase-app';

// Use initializeFirestore (not getFirestore) so we can pass settings.
// experimentalAutoDetectLongPolling: Firestore probes the connection and
// falls back to long-polling when WebChannel streaming is blocked — which
// happens in Safari (ITP), corporate proxies, and some VPNs. Chrome/Edge
// continue to use WebChannel because the probe succeeds.
// This must run before any other code calls getFirestore(app); the shim is
// the single entry point for Firestore on web (via webpack alias), so that
// ordering is guaranteed.
let db;
try {
  db = initializeFirestore(app, {
    experimentalAutoDetectLongPolling: true,
  });
} catch {
  // If initializeFirestore was already called (e.g. HMR in dev), fall back to
  // the existing instance so hot reloads do not throw.
  db = _getFirestore(app);
}

const wrapQuery = (queryRef: any) => ({
  where: (field: string, op: any, value: any) =>
    wrapQuery(_query(queryRef, _where(field, op, value))),
  limit: (count: number) => wrapQuery(_query(queryRef, _limit(count))),
  get: () => _getDocs(queryRef),
  onSnapshot: (callback: any, errorCallback?: any) =>
    _onSnapshot(queryRef, callback, errorCallback),
});

// Re-export modular API (same signatures as Firebase JS SDK)
export const getFirestore = () => db;
export const collection = _collection;
export const doc = _doc;
export const query = _query;
export const where = _where;
export const getDocs = _getDocs;
export const getDoc = _getDoc;
export const addDoc = _addDoc;
export const updateDoc = _updateDoc;
export const deleteDoc = _deleteDoc;
export const onSnapshot = _onSnapshot;
export const serverTimestamp = _serverTimestamp;
export const writeBatch = _writeBatch;
export const setDoc = _setDoc;

// Namespace API wrapper: firestore().collection('x').doc('y').get()
const firestoreNamespace = () => ({
  collection: (path: string) => {
    const ref = _collection(db, path);
    return {
      doc: (id: string) => {
        const docRef = _doc(db, path, id);
        return {
          get: () => _getDoc(docRef),
          set: (data: any, options?: any) => _setDoc(docRef, data, options || {}),
          update: (data: any) => _updateDoc(docRef, data),
          delete: () => _deleteDoc(docRef),
          onSnapshot: (callback: any, errorCallback?: any) =>
            _onSnapshot(docRef, callback, errorCallback),
          id: id,
          path: docRef.path,
        };
      },
      add: (data: any) => _addDoc(ref, data),
      where: (field: string, op: any, value: any) =>
        wrapQuery(_query(ref, _where(field, op, value))),
      limit: (count: number) => wrapQuery(_query(ref, _limit(count))),
      onSnapshot: (callback: any, errorCallback?: any) =>
        _onSnapshot(ref, callback, errorCallback),
      get: () => _getDocs(ref),
    };
  },
  batch: () => _writeBatch(db),
});

export {Timestamp};

firestoreNamespace.Timestamp = Timestamp;
firestoreNamespace.FieldValue = {
  serverTimestamp: () => _serverTimestamp(),
  arrayUnion: (...elements: any[]) => _arrayUnion(...elements),
  arrayRemove: (...elements: any[]) => _arrayRemove(...elements),
  delete: () => _deleteField(),
};

export default firestoreNamespace;
